import { useEffect, useRef } from 'react'
import { ChevronRight, X } from 'lucide-react'
import type { Actor, Incident, Target } from '@/types/map'
import {
  clockUTC,
  dateUTC,
  incidentAttributionQualifier,
  incidentStatusVar,
  severityVar,
} from '../map-display-utils'
import { ProvenanceDetails, ReasonChips } from '../ProjectionProvenance'

export interface IncidentDrawerProps {
  incident: Incident
  actor: Actor | null
  target: Target | null
  onClose: () => void
  onOpenActor: (actorId: string) => void
}

export function IncidentDrawer({
  incident,
  actor,
  target,
  onClose,
  onOpenActor,
}: IncidentDrawerProps) {
  const ref = useRef<HTMLElement>(null)

  useEffect(() => {
    ref.current?.focus()
  }, [])

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <aside
      ref={ref}
      role="dialog"
      aria-label={`Incident ${incident.id}`}
      tabIndex={-1}
      className="fixed right-0 top-0 z-50 flex h-screen w-[480px] max-w-[92vw] flex-col border-l border-[color:var(--border-default)] bg-[color:var(--surface-elevated)] shadow-lg focus:outline-none"
      data-testid="incident-drawer"
    >
      {/* Header */}
      <header className="shrink-0 border-b border-[color:var(--border-subtle)] p-4">
        <div className="flex items-start justify-between">
          <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
            Incident Detail
          </span>
          <button
            onClick={onClose}
            aria-label="Close drawer"
            className="-mr-1 -mt-1 inline-flex h-7 w-7 items-center justify-center rounded-md text-[color:var(--text-secondary)] transition-colors hover:bg-[color:var(--surface-tertiary)] hover:text-[color:var(--text-primary)]"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <SeverityChip severity={incident.severity} />
          <StatusPill status={incident.status} />
          <span className="ml-auto font-mono text-sm text-[color:var(--text-secondary)]">
            {incident.id}
          </span>
        </div>
        {target && (
          <h2 className="mt-3 font-heading text-lg leading-tight text-[color:var(--text-primary)]">
            {target.name}
          </h2>
        )}
      </header>

      {/* Body */}
      <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-sm">
        {actor ? (
          <section>
            <SectionLabel>Attribution</SectionLabel>
            <button
              type="button"
              onClick={() => onOpenActor(actor.id)}
              className="mt-1.5 flex w-full items-center gap-3 rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-secondary)] p-3 text-left transition-colors hover:border-[color:var(--interactive-primary)]"
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium text-[color:var(--text-primary)]">
                  {actor.name}
                </span>
                {actor.aliases.length > 0 && (
                  <span className="mt-0.5 block truncate font-mono text-[10px] uppercase tracking-[0.14em] text-[color:var(--text-tertiary)]">
                    aka {actor.aliases.join(', ')}
                  </span>
                )}
              </span>
              <ChevronRight
                className="h-4 w-4 shrink-0 text-[color:var(--text-tertiary)]"
                aria-hidden
              />
            </button>
          </section>
        ) : (
          <section>
            <SectionLabel>Attribution</SectionLabel>
            <div className="mt-1.5 rounded-md border border-dashed border-[color:var(--border-subtle)] px-3 py-2">
              <p className="font-mono text-[11px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
                {incident.ransomwareGroupName || 'Unattributed'}
              </p>
              {incident.ransomwareGroupName && (
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
                  {incidentAttributionQualifier(incident) ?? 'Known ransomware group'}
                </p>
              )}
            </div>
          </section>
        )}

        <section>
          <SectionLabel>Telemetry</SectionLabel>
          <dl className="mt-1.5 divide-y divide-[color:var(--border-subtle)] border-y border-[color:var(--border-subtle)]">
            <Field label="Started">
              {dateUTC(incident.startedAt)} {clockUTC(incident.startedAt)}Z
            </Field>
            {target && <Field label="Target">{target.name}</Field>}
            {target && <Field label="Sector">{target.sector}</Field>}
            {target && <Field label="Location">{target.countryIso2}</Field>}
            {incident.mitreTechniques.length > 0 && (
              <Field label="Technique">{incident.mitreTechniques.join(', ')}</Field>
            )}
            {incident.cveIds.length > 0 && (
              <Field label="CVE">{incident.cveIds.join(', ')}</Field>
            )}
            <Field label="Confidence">{incident.confidenceLabel}</Field>
            {incident.sourceKind && <Field label="Source">{incident.sourceKind}</Field>}
            {incident.attributionState && <Field label="Attrib state">{incident.attributionState}</Field>}
          </dl>
        </section>

        {(incident.unknownReasons?.length ||
          incident.provenance?.length ||
          incident.availableButNotSelected?.length ||
          incident.sourceClaimId) && (
          <section>
            <SectionLabel>Projection provenance</SectionLabel>
            <div className="mt-1.5 space-y-2">
              <ReasonChips reasons={incident.unknownReasons} label="Incident projection reasons" />
              <ProvenanceDetails
                provenance={incident.provenance}
                availableButNotSelected={incident.availableButNotSelected}
                evidenceIds={[
                  { label: 'Incident', value: incident.id },
                  { label: 'Claim', value: incident.sourceClaimId },
                ]}
              />
            </div>
          </section>
        )}

        {incident.narrative && (
          <section>
            <SectionLabel>Summary</SectionLabel>
            <p className="mt-1.5 leading-relaxed text-[color:var(--text-secondary)]">
              {incident.narrative}
            </p>
          </section>
        )}
      </div>

      {/* Footer */}
      <footer className="shrink-0 border-t border-[color:var(--border-subtle)] p-4">
        <a
          href={`/stories/${incident.id}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-[color:var(--interactive-primary)] hover:underline"
        >
          View full story
          <ChevronRight className="h-4 w-4" aria-hidden />
        </a>
      </footer>
    </aside>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="font-mono text-[10px] uppercase tracking-[0.18em] text-[color:var(--text-tertiary)]">
      {children}
    </h3>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-baseline gap-3 py-1.5">
      <dt className="w-24 shrink-0 font-mono text-[10px] uppercase tracking-[0.12em] text-[color:var(--text-tertiary)]">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 break-words font-mono text-xs text-[color:var(--text-primary)]">
        {children}
      </dd>
    </div>
  )
}

function SeverityChip({ severity }: { severity: Incident['severity'] }) {
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[10px] font-medium uppercase tracking-[0.12em]"
      style={{
        borderColor: severityVar(severity),
        color: severityVar(severity),
        backgroundColor: `color-mix(in oklch, ${severityVar(severity)} 14%, transparent)`,
      }}
    >
      <span
        className="h-1.5 w-1.5 rounded-full"
        style={{ background: severityVar(severity) }}
      />
      {severity}
    </span>
  )
}

function StatusPill({ status }: { status: Incident['status'] }) {
  return (
    <span
      className="inline-flex items-center rounded-full border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em]"
      style={{
        borderColor: 'var(--border-default)',
        color: incidentStatusVar(status),
      }}
    >
      {status}
    </span>
  )
}
